import { useState, useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { upsertAnnotation, setFields } from '../../../../../store/slices/documentsSlice'
import type { Annotation } from '../../../../ai-viewer/eventing/types'

interface UseAnnotationManagerParams {
  currentPage: number
  onPageChange: (page: number) => void
  onUrlUpdate: (params: Record<string, string | null>) => void
}

interface UseAnnotationManagerResult {
  selectedAnnotationId: string | null
  currentAnnotationIndex: number
  handleAnnotationUpdate: (updated: Annotation) => void
  handleAnnotationCreate: (created: Annotation) => void
  handleNextAnnotation: () => void
  handlePrevAnnotation: () => void
  setSelectedAnnotationId: (id: string | null) => void
}

export function useAnnotationManager({
  currentPage,
  onPageChange,
  onUrlUpdate
}: UseAnnotationManagerParams): UseAnnotationManagerResult {
  const dispatch = useAppDispatch()
  
  // Read directly from Redux - single source of truth
  const annotations = useAppSelector(state => state.documents.annotations)
  const fields = useAppSelector(state => state.documents.fields)
  
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null)
  const [currentAnnotationIndex, setCurrentAnnotationIndex] = useState(0)

  // Reset annotation index when annotations change
  useEffect(() => {
    if (annotations.length === 0) {
      setCurrentAnnotationIndex(0)
      setSelectedAnnotationId(null)
    } else if (currentAnnotationIndex >= annotations.length) {
      setCurrentAnnotationIndex(Math.max(0, annotations.length - 1))
    }
  }, [annotations.length, currentAnnotationIndex])

  const handleAnnotationUpdate = useCallback((updated: Annotation) => {
    // Update Redux (single source of truth)
    dispatch(upsertAnnotation(updated))
    
    // Select the annotation to show editor
    setSelectedAnnotationId(updated.id)
    
    // If annotation was accepted, update related fields
    if (updated.status === 'accepted') {
      const linkedFields = updated.links?.map(l => l.entity_key).filter(Boolean) || []
      const updatedFields = fields.map(f => 
        linkedFields.includes(f.key) 
          ? { ...f, status: 'verified' as const, confidence: Math.max(f.confidence || 0, 0.9) }
          : f
      )
      dispatch(setFields(updatedFields))
    }
  }, [dispatch, fields])

  const handleAnnotationCreate = useCallback((created: Annotation) => {
    // Add to Redux (single source of truth)
    dispatch(upsertAnnotation(created))
    
    // Select the new annotation to show editor
    setSelectedAnnotationId(created.id)
  }, [dispatch])

  const handleNextAnnotation = useCallback(() => {
    if (annotations.length > 0) {
      const nextIndex = (currentAnnotationIndex + 1) % annotations.length
      const nextAnnotation = annotations[nextIndex]
      
      setCurrentAnnotationIndex(nextIndex)
      setSelectedAnnotationId(nextAnnotation.id)
      
      // Navigate to the page if it's different
      if (nextAnnotation.rect && nextAnnotation.rect.page !== currentPage) {
        onPageChange(nextAnnotation.rect.page)
      }
      
      // Update URL
      onUrlUpdate({
        page: (nextAnnotation.rect?.page || nextAnnotation.page)?.toString() || null,
        annotation: nextAnnotation.id,
        highlight: nextAnnotation.id
      })
    }
  }, [annotations, currentAnnotationIndex, currentPage, onPageChange, onUrlUpdate])

  const handlePrevAnnotation = useCallback(() => {
    if (annotations.length > 0) {
      const prevIndex = currentAnnotationIndex === 0 ? annotations.length - 1 : currentAnnotationIndex - 1
      const prevAnnotation = annotations[prevIndex]
      
      setCurrentAnnotationIndex(prevIndex)
      setSelectedAnnotationId(prevAnnotation.id)
      
      // Navigate to the page if it's different
      if (prevAnnotation.rect && prevAnnotation.rect.page !== currentPage) {
        onPageChange(prevAnnotation.rect.page)
      }
      
      // Update URL
      onUrlUpdate({
        page: (prevAnnotation.rect?.page || prevAnnotation.page)?.toString() || null,
        annotation: prevAnnotation.id,
        highlight: prevAnnotation.id
      })
    }
  }, [annotations, currentAnnotationIndex, currentPage, onPageChange, onUrlUpdate])

  return {
    selectedAnnotationId,
    currentAnnotationIndex,
    handleAnnotationUpdate,
    handleAnnotationCreate,
    handleNextAnnotation,
    handlePrevAnnotation,
    setSelectedAnnotationId
  }
}

